import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  Tag,
  Statistic,
  Row,
  Col,
  Empty,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { PageHeader } from '../components/PageHeader';
import { api, SettlementRun } from '../api';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const { Text } = Typography;

const AdminSettlementOverview = () => {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<SettlementRun[]>([]);
  const [summary, setSummary] = useState({
    total_runs: 0,
    succeeded: 0,
    failed: 0,
    running: 0,
    scheduled: 0,
    last_run: null as string | null,
    last_success: null as string | null,
  });

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const data = await api.settlements.getOverview();
      setRuns(data.runs);
      setSummary(data.summary);
    } catch (error: any) {
      console.error('Failed to fetch settlement overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'succeeded':
        return <Tag color="success" icon={<CheckCircleOutlined />}>SUCCEEDED</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>FAILED</Tag>;
      case 'running':
        return <Tag color="processing" icon={<LoadingOutlined />}>RUNNING</Tag>;
      case 'scheduled':
        return <Tag color="default" icon={<ClockCircleOutlined />}>SCHEDULED</Tag>;
      default:
        return <Tag>{status.toUpperCase()}</Tag>;
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return '—';
    const d = dayjs.duration(seconds, 'seconds');
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${d.minutes()}m ${d.seconds()}s`;
    return `${d.hours()}h ${d.minutes()}m`;
  };

  const columns = [
    {
      title: 'Week',
      dataIndex: 'run_key',
      key: 'run_key',
      render: (key: string, record: SettlementRun) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{key}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(record.week_ending_at).format('MMM D, YYYY')}
          </Text>
        </Space>
      ),
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
      width: 130,
    },
    {
      title: 'Duration',
      dataIndex: 'duration_seconds',
      key: 'duration',
      render: (seconds: number | null) => formatDuration(seconds),
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Users Processed',
      key: 'users',
      render: (_: any, record: SettlementRun) => {
        const count = record.result?.users_processed;
        return count !== undefined ? count : '—';
      },
      width: 130,
      align: 'right' as const,
    },
    {
      title: 'Total Distributed',
      key: 'distributed',
      render: (_: any, record: SettlementRun) => {
        const cents = record.result?.total_distributed_cents;
        if (cents === undefined) return '—';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(cents / 100);
      },
      width: 150,
      align: 'right' as const,
    },
    {
      title: 'Snapshots',
      key: 'snapshots',
      render: (_: any, record: SettlementRun) => {
        const count = record.result?.snapshots_created;
        return count !== undefined ? count : '—';
      },
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Trigger',
      dataIndex: 'trigger_source',
      key: 'trigger',
      render: (source: string) => {
        const color = source === 'catch_up' ? 'warning' : 'default';
        return <Tag color={color}>{source.toUpperCase()}</Tag>;
      },
      width: 100,
    },
    {
      title: 'Error',
      key: 'error',
      render: (_: any, record: SettlementRun) => {
        if (!record.error) return '—';
        return (
          <Text type="danger" style={{ fontSize: '12px' }}>
            {record.error.message.substring(0, 50)}...
          </Text>
        );
      },
      width: 200,
    },
  ];

  const successRate = summary.total_runs > 0
    ? ((summary.succeeded / summary.total_runs) * 100).toFixed(1)
    : '0.0';

  return (
    <>
      <PageHeader
        title="Weekly Settlement Overview"
        description="Monitor automated weekly profit settlement runs"
      />

      {/* Summary Statistics */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Runs"
              value={summary.total_runs}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={successRate}
              suffix="%"
              styles={{ content: { color: Number(successRate) >= 95 ? '#52c41a' : '#faad14' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Last Run"
              value={summary.last_run ? dayjs(summary.last_run).format('MMM D') : 'Never'}
              styles={{ content: { fontSize: '20px' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Last Success"
              value={summary.last_success ? dayjs(summary.last_success).format('MMM D') : 'Never'}
              styles={{ content: { fontSize: '20px', color: '#52c41a' } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Status Breakdown */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="large">
          <Statistic
            title="Succeeded"
            value={summary.succeeded}
            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            styles={{ content: { color: '#52c41a' } }}
          />
          <Statistic
            title="Failed"
            value={summary.failed}
            prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            styles={{ content: { color: '#ff4d4f' } }}
          />
          <Statistic
            title="Running"
            value={summary.running}
            prefix={<LoadingOutlined style={{ color: '#1890ff' }} />}
            styles={{ content: { color: '#1890ff' } }}
          />
          <Statistic
            title="Scheduled"
            value={summary.scheduled}
            prefix={<ClockCircleOutlined />}
          />
        </Space>
      </Card>

      {/* Alert for failures */}
      {summary.failed > 0 && (
        <Alert
          type="warning"
          title="Settlement Failures Detected"
          description={`${summary.failed} settlement run(s) failed. Review error messages below and check logs for details.`}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Runs Table */}
      <Card title="Settlement Run History (Last 8)">
        <Table
          columns={columns}
          dataSource={runs}
          loading={loading}
          pagination={false}
          rowKey="id"
          locale={{
            emptyText: (
              <Empty
                description="No settlement runs yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          scroll={{ x: 1100 }}
        />
      </Card>
    </>
  );
};

export default AdminSettlementOverview;
